import { z } from "zod";
import { AdminReason } from "./admin.ts";
import { TeacherInviteEmail, TeacherInviteState } from "./admin-invites.ts";
import { Printable } from "./feedback.ts";
import { ALL_SUBJECTS, Subject } from "./subjects.ts";
import { DisplayName, UserId } from "./user.ts";

/**
 * «Позвать коллегу»: заявка преподавателя владельцу (проект целиком в
 * `docs/colleague-referral-design.md`).
 *
 * Преподаватель оставляет имя и контакт коллеги, админ выпускает по заявке
 * обычное приглашение или закрывает её без приглашения. Здесь контракт ручки
 * кабинета, контракты панели и три чистые проверки ввода, общие для схемы,
 * ручки и формы (3.2).
 *
 * ⚠️ Имя, контакт и комментарий это ПДн третьего лица, и живут они только у
 * открытой заявки: закрытие любым исходом их стирает, и держит это `CHECK`
 * базы, а не код (2.1). Поэтому в ответах панели у закрытой заявки эти поля
 * `null` по построению.
 *
 * Корневой подпуть: ответов к заданиям здесь нет, барьеры 5.3 CLAUDE.md не
 * задеты.
 */

export const REFERRAL_CONTACT_KINDS = ["email", "telegram", "phone", "other"] as const;
export const ReferralContactKind = z.enum(REFERRAL_CONTACT_KINDS);
export type ReferralContactKind = z.infer<typeof ReferralContactKind>;

/** Имя коллеги: та же граница, что у имени контакта в анкете (`CONTACT_NAME_MAX`). */
export const REFERRAL_NAME_MAX = 80;
/** Потолок контакта: потолок почты в приглашении (`teacher_invite_email_len`). */
export const REFERRAL_CONTACT_MAX = 320;
/** Свободная строка «другое»: одной ссылки хватает. */
export const REFERRAL_OTHER_MAX = 200;
export const REFERRAL_NOTE_MAX = 500;
/** Открытых заявок на преподавателя (решение 3). */
export const REFERRAL_OPEN_MAX = 5;
/** Срок жизни открытой заявки, сутки (решение 14.10.2026). */
export const REFERRAL_TTL_DAYS = 60;
/** Страница закрытых заявок в панели. */
export const ADMIN_REFERRAL_PAGE_SIZE = 100;
/** Потолок списка открытых: дальше `truncated`. */
export const ADMIN_REFERRAL_OPEN_CAP = 500;

/* ──────────────────────── проверки ввода ──────────────────────── */

export type ReferralContactProblem =
  | "empty"
  | "email_shape"
  | "telegram_shape"
  | "phone_shape"
  | "too_long"
  | "control_chars";

/**
 * Управляющие: всё, что отвергает `Printable`, плюс `\t`, `\n`, `\r`, C1
 * (U+0080-U+009F) и разделители строк U+2028 и U+2029. Перевод строки в имени
 * ломает строку таблицы панели и строку «Кому отправить».
 */
const CONTROL = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;
/**
 * Направление текста: переворачивают показ соседнего текста, то есть значение
 * в панели выглядело бы не тем, что лежит в базе.
 */
const BIDI = /[\u200e\u200f\u061c\u202a-\u202e\u2066-\u2069]/;
const BIDI_ALL = /[\u200e\u200f\u061c\u202a-\u202e\u2066-\u2069]/g;
/** Нулевая ширина: вырезается молча везде, глазом её не видно и не стереть. */
const ZERO_WIDTH = /[\u200b-\u200d\u2060\ufeff]/g;

/**
 * Имя коллеги. Управляющие и символы направления это отказ, нулевая ширина
 * вырезается, дальше `trim`.
 */
export function checkReferralName(
  raw: string,
): { ok: true; value: string } | { ok: false; problem: "empty" | "too_long" | "control_chars" } {
  if (CONTROL.test(raw) || BIDI.test(raw)) return { ok: false, problem: "control_chars" };
  const value = raw.replace(ZERO_WIDTH, "").trim();
  if (value.length === 0) return { ok: false, problem: "empty" };
  if (value.length > REFERRAL_NAME_MAX) return { ok: false, problem: "too_long" };
  return { ok: true, value };
}

/**
 * Комментарий. Необязателен: пустое после вырезания и `trim` это `null`, а не
 * отказ. Переводы строк законны (`\n` и `\r\n`, второй хранится первым),
 * остальные управляющие и символы направления это отказ.
 */
export function checkReferralNote(
  raw: string | undefined,
): { ok: true; value: string | null } | { ok: false; problem: "too_long" | "control_chars" } {
  if (raw === undefined) return { ok: true, value: null };
  const lines = raw.replace(/\r\n/g, "\n");
  if (CONTROL.test(lines.replace(/\n/g, "")) || BIDI.test(lines)) {
    return { ok: false, problem: "control_chars" };
  }
  const value = lines.replace(ZERO_WIDTH, "").trim();
  if (value.length === 0) return { ok: true, value: null };
  if (value.length > REFERRAL_NOTE_MAX) return { ok: false, problem: "too_long" };
  return { ok: true, value };
}

/**
 * Ник Telegram по правилам Telegram: латиница, цифры и `_`, от 5 до 32 знаков,
 * первая буква, на `_` не кончается. Та же форма стоит в `CHECK` миграции.
 */
const TELEGRAM_NICK = /^[A-Za-z][A-Za-z0-9_]{3,30}[A-Za-z0-9]$/;
/**
 * Ссылки, которые вставляют копированием: `t.me/ник`, `https://t.me/ник`,
 * `http://`, `www.`, `telegram.me` и косая черта в конце. Приглашение в чат
 * (`t.me/+…`, `t.me/joinchat/…`) и ссылка на сообщение (`t.me/ник/123`) сюда
 * не проходят: это не человек.
 */
const TELEGRAM_LINK = /^(?:(?:https?:\/\/)?(?:www\.)?(?:t|telegram)\.me\/)?@?([^/@]+)\/?$/i;
/** Телефон до нормализации: цифры, пробелы, скобки, дефисы, точки, `+` только первым. */
const PHONE_RAW = /^\+?[0-9\s().-]+$/;

/**
 * Контакт по типу: значение, которое ложится в базу, или проблема.
 *
 * ⚠️ Её зовут ТРИ места (схема, ручка, форма кабинета), и в этом смысл одной
 * функции: две нормализации разъезжаются, и тогда контакт проходит форму и
 * падает на `CHECK` в базе.
 *
 * ⚠️ У почты, ника и телефона символы направления вырезаются молча, а не
 * отказывают: iOS оборачивает ими скопированный номер (U+202A и U+202C), и
 * подсказка «невидимые символы» на номере из контактов была бы загадкой. Форма
 * значения там жёсткая, после вырезания проверять больше нечего. У «другого»
 * форма свободная, и отказ честнее молчаливой правки того, что человек видит.
 */
export function normalizeReferralContact(
  kind: ReferralContactKind,
  raw: string,
): { ok: true; value: string } | { ok: false; problem: ReferralContactProblem } {
  if (CONTROL.test(raw)) return { ok: false, problem: "control_chars" };
  if (kind === "other" && BIDI.test(raw)) return { ok: false, problem: "control_chars" };
  const value = raw.replace(BIDI_ALL, "").replace(ZERO_WIDTH, "").trim();
  if (value.length === 0) return { ok: false, problem: "empty" };

  switch (kind) {
    case "email": {
      /**
       * ⚠️ ТЕМ ЖЕ валидатором, что `sentToEmail` выпуска: панель подставляет
       * контакт заявки в форму выпуска, и адрес, прошедший заявку и не
       * прошедший выпуск, это отказ админу в самый неподходящий момент.
       */
      const parsed = TeacherInviteEmail.safeParse(value);
      if (!parsed.success) return { ok: false, problem: "email_shape" };
      const at = parsed.data.lastIndexOf("@");
      return { ok: true, value: parsed.data.slice(0, at) + parsed.data.slice(at).toLowerCase() };
    }
    case "telegram": {
      const nick = TELEGRAM_LINK.exec(value)?.[1];
      if (nick === undefined || !TELEGRAM_NICK.test(nick)) {
        return { ok: false, problem: "telegram_shape" };
      }
      return { ok: true, value: `@${nick}` };
    }
    case "phone": {
      if (!PHONE_RAW.test(value)) return { ok: false, problem: "phone_shape" };
      const digits = value.replace(/[^0-9]/g, "");
      if (digits.length < 5 || digits.length > 15) return { ok: false, problem: "phone_shape" };
      if (value.startsWith("+")) return { ok: true, value: `+${digits}` };
      /**
       * ⚠️ `8 916 …` превращается в `+7 916 …`, и только он (вместе с `7 916 …`
       * без плюса). Десятизначный номер без кода не трогается: дописать ему
       * `+7` значит угадать страну.
       */
      if (digits.length === 11 && (digits.startsWith("8") || digits.startsWith("7"))) {
        return { ok: true, value: `+7${digits.slice(1)}` };
      }
      return { ok: true, value: digits };
    }
    case "other":
      if (value.length > REFERRAL_OTHER_MAX) return { ok: false, problem: "too_long" };
      return { ok: true, value };
  }
}

/* ──────────────────────── кабинет ──────────────────────── */

/**
 * Тело `POST /v1/colleague-referrals`. Имя и контакт приходят СЫРЫМИ: потолки
 * тут вдвое шире нормы и стоят только против мусора, а правило держат три
 * проверки выше через `superRefine`. Ошибка ложится в путь своего поля.
 */
export const CreateColleagueReferralRequest = z
  .object({
    colleagueName: z.string().max(REFERRAL_NAME_MAX * 2),
    contactKind: ReferralContactKind,
    contact: z.string().max(REFERRAL_CONTACT_MAX * 2),
    subjects: z.array(Subject).max(8).default([]),
    /**
     * Комментарий тоже сырой, как имя: настоящий потолок меряет
     * `checkReferralNote` ПОСЛЕ вырезания нулевой ширины и склейки `\r\n`,
     * ровно как форма. Мерка здесь до нормализации отказала бы тексту, который
     * форма пропустила.
     */
    note: Printable.max(REFERRAL_NOTE_MAX * 2).optional(),
  })
  .superRefine((b, ctx) => {
    const name = checkReferralName(b.colleagueName);
    if (!name.ok) ctx.addIssue({ code: "custom", message: name.problem, path: ["colleagueName"] });
    const contact = normalizeReferralContact(b.contactKind, b.contact);
    if (!contact.ok) ctx.addIssue({ code: "custom", message: contact.problem, path: ["contact"] });
    const note = checkReferralNote(b.note);
    if (!note.ok) ctx.addIssue({ code: "custom", message: note.problem, path: ["note"] });
  });
export type CreateColleagueReferralRequest = z.infer<typeof CreateColleagueReferralRequest>;

/** Строка заявки в том виде, в каком она ложится в базу. */
export interface ReferralToStore {
  colleagueName: string;
  contactKind: ReferralContactKind;
  contactValue: string;
  subjects: Subject[];
  note: string | null;
}

/**
 * Разобранное тело в значения для базы: имя после проверки, контакт после
 * нормализации, предметы без повторов в порядке `ALL_SUBJECTS`, пустая заметка
 * это `null`.
 *
 * Зовётся после схемы, поэтому отказ здесь недостижим; если однажды станет
 * достижим (схему ослабили), это исключение, а не тихая запись сырого ввода.
 */
export function referralToStore(body: CreateColleagueReferralRequest): ReferralToStore {
  const name = checkReferralName(body.colleagueName);
  const contact = normalizeReferralContact(body.contactKind, body.contact);
  const note = checkReferralNote(body.note);
  if (!name.ok || !contact.ok || !note.ok) {
    throw new Error("заявка не прошла проверку: сначала схема");
  }
  return {
    colleagueName: name.value,
    contactKind: body.contactKind,
    contactValue: contact.value,
    subjects: ALL_SUBJECTS.filter((s) => body.subjects.includes(s)),
    note: note.value,
  };
}

/* ──────────────────────── панель ──────────────────────── */

export const AdminReferralState = z.enum([
  "open",
  "invited",
  "dismissed",
  "referrer_gone",
  "expired",
]);
export type AdminReferralState = z.infer<typeof AdminReferralState>;

/**
 * Заявка в панели.
 *
 * `invite` несёт ровно те поля, которые читает компонент `InviteState` экрана
 * приглашений: панель показывает состояние тем же компонентом, а не вторым.
 */
export const AdminReferralView = z.object({
  id: z.string().uuid(),
  createdAt: z.number().int(),
  referrer: z.object({ userId: UserId, displayName: DisplayName, blocked: z.boolean() }),
  subjects: z.array(Subject),
  state: AdminReferralState,
  // null ровно у закрытой
  colleagueName: z.string().nullable(),
  contactKind: ReferralContactKind.nullable(),
  contact: z.string().nullable(),
  note: z.string().nullable(),
  closedAt: z.number().int().nullable(),
  /** `null` у открытой и у исходов уборщика (`referrer_gone`, `expired`). */
  closedBy: z.object({ userId: UserId, displayName: DisplayName }).nullable(),
  invite: z
    .object({
      id: z.string().uuid(),
      state: TeacherInviteState,
      redeemedAt: z.number().int().nullable(),
      redeemedUser: z.object({ userId: UserId, displayName: DisplayName }).nullable(),
      revokedAt: z.number().int().nullable(),
    })
    .nullable(),
});
export type AdminReferralView = z.infer<typeof AdminReferralView>;

export const AdminReferralListResponse = z.object({
  referrals: z.array(AdminReferralView),
  /** Только у закрытых. */
  nextCursor: z.string().uuid().nullable(),
  /** Только у открытых: потолок `ADMIN_REFERRAL_OPEN_CAP`. */
  truncated: z.boolean(),
});
export type AdminReferralListResponse = z.infer<typeof AdminReferralListResponse>;

/** Счётчик в шапке панели. */
export const AdminReferralSummary = z.object({ open: z.number().int().min(0) });
export type AdminReferralSummary = z.infer<typeof AdminReferralSummary>;

/** Закрыть без приглашения: причина обязательна, как у каждого пишущего действия панели. */
export const AdminReferralDismissRequest = z.object({ reason: AdminReason });
export type AdminReferralDismissRequest = z.infer<typeof AdminReferralDismissRequest>;
